import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { X, Download, Save, Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';

const enconOrange = '#ee7203';

// In-progress edits are kept in sessionStorage so closing the modal (or
// clicking outside) never loses anything.
const VIEWER_DRAFT_KEY = 'disciplinary_viewer_draft_v1';

/* --------------------------------------------------------------------------
 * AutoGrowTextarea — keeps an internal local value so typing is smooth.
 * Commits to parent on every keystroke (React.memo on the surrounding rows
 * keeps this cheap) so the parent state is always up-to-date — important
 * because reorders read the latest parent state to decide what to move.
 * ------------------------------------------------------------------------ */
const AutoGrowTextarea = React.memo(function AutoGrowTextarea({
  initialValue,
  onCommit,
  className = '',
  placeholder,
  testid,
  highlightStrike = false,
}) {
  const [val, setVal] = useState(initialValue || '');
  const ref = useRef(null);
  const lastCommittedRef = useRef(initialValue || '');

  // Pick up external resets (different script loaded) without losing
  // the user's in-progress local edits in the common case.
  useEffect(() => {
    if ((initialValue || '') !== lastCommittedRef.current && (initialValue || '') !== val) {
      setVal(initialValue || '');
      lastCommittedRef.current = initialValue || '';
    }
  }, [initialValue, val]);

  // Adjust height to content. Runs only when local `val` changes.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [val]);

  const handleChange = (e) => {
    const next = e.target.value;
    setVal(next);
    lastCommittedRef.current = next;
    if (onCommit) onCommit(next);
  };

  return (
    <textarea
      ref={ref}
      value={val}
      onChange={handleChange}
      placeholder={placeholder}
      rows={1}
      className={`${className} ${highlightStrike ? 'line-through text-gray-500' : ''}`}
      style={{ overflow: 'hidden', resize: 'none' }}
      data-testid={testid}
    />
  );
});

/* --------------------------------------------------------------------------
 * Item row — memoized so typing in one item never re-renders the others.
 * Shows an editable sequence number on the left (1-based within its section);
 * changing the number moves the item to that position.
 * ------------------------------------------------------------------------ */
const ItemRow = React.memo(function ItemRow({
  item,
  index,       // 0-based position within its section
  total,       // total items in this section
  ticked,
  onToggleTick,
  onCommitText,
  onReorder,   // (newIndex) => void
  onDelete,
}) {
  const [numDraft, setNumDraft] = useState(String(index + 1));
  const commitTimerRef = useRef(null);

  // Keep the number input synced when this row's index actually changes
  // (e.g. after a reorder). Only resync when the incoming index differs
  // from the last-committed value so we don't fight with the user mid-type.
  useEffect(() => {
    setNumDraft(String(index + 1));
  }, [index]);

  const doCommit = (rawValue) => {
    const n = parseInt(rawValue, 10);
    if (isNaN(n)) {
      setNumDraft(String(index + 1));
      return;
    }
    if (n === index + 1) return;   // no change
    const clamped = Math.max(1, Math.min(total, n));
    onReorder(clamped - 1);
    // The index prop change will trigger the sync effect above.
  };

  const handleChange = (e) => {
    // Allow only digits; keep empty string while typing so users can
    // clear-and-retype easily.
    const v = e.target.value.replace(/[^\d]/g, '');
    setNumDraft(v);
    // Debounce the commit so a reorder happens ~350ms after the user
    // stops typing, without them needing to hit Enter or Tab.
    if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
    commitTimerRef.current = setTimeout(() => {
      if (v !== '') doCommit(v);
    }, 350);
  };

  const handleBlur = () => {
    if (commitTimerRef.current) { clearTimeout(commitTimerRef.current); commitTimerRef.current = null; }
    if (numDraft === '') { setNumDraft(String(index + 1)); return; }
    doCommit(numDraft);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.target.blur();
    }
  };

  return (
    <li
      className={`group flex items-start gap-2 p-2 rounded-lg border ${
        ticked ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200 hover:border-orange-200'
      }`}
    >
      <input
        type="checkbox"
        checked={!!ticked}
        onChange={onToggleTick}
        className="mt-1.5 w-4 h-4 accent-orange-500 shrink-0"
        data-testid={`disc-tick-${item.id}`}
      />
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={numDraft}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onFocus={(e) => e.target.select()}
        className="mt-0.5 w-11 text-center text-sm font-bold text-orange-700 bg-orange-50 border border-orange-200 rounded-md py-1 shrink-0 focus:outline-none focus:ring-2 focus:ring-orange-300"
        title="Type a number to move this item"
        data-testid={`disc-num-${item.id}`}
      />
      <div className="flex-1 min-w-0">
        <AutoGrowTextarea
          initialValue={item.text}
          onCommit={onCommitText}
          highlightStrike={ticked}
          placeholder={item.type === 'question' ? 'Type a question…' : 'Type a statement to read aloud…'}
          className="w-full bg-transparent border-none outline-none text-sm text-gray-900"
          testid={`disc-item-text-${item.id}`}
        />
        <span
          className={`inline-block text-[10px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${
            item.type === 'question' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'
          }`}
        >
          {item.type}
        </span>
      </div>
      <button
        type="button"
        onClick={onDelete}
        className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600 transition-opacity"
        title="Delete"
        data-testid={`disc-delete-${item.id}`}
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </li>
  );
});

/* --------------------------------------------------------------------------
 * Section — also memoized so editing one section doesn't re-render the others.
 * ------------------------------------------------------------------------ */
const Section = React.memo(function Section({
  section,
  savedTick,
  onAddItem,
  onDeleteItem,
  onCommitItemText,
  onToggleTick,
  onReorderItem,
}) {
  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-extrabold uppercase tracking-wide" style={{ color: enconOrange }}>
          {section.title}
        </h4>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => onAddItem(section.id, 'question')}
            className="text-xs px-2.5 py-1 rounded-md border border-orange-300 text-orange-700 hover:bg-orange-50 inline-flex items-center gap-1"
            data-testid={`disc-add-question-${section.id}`}
          >
            <Plus className="w-3 h-3" /> Question
          </button>
          <button
            type="button"
            onClick={() => onAddItem(section.id, 'statement')}
            className="text-xs px-2.5 py-1 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 inline-flex items-center gap-1"
            data-testid={`disc-add-statement-${section.id}`}
          >
            <Plus className="w-3 h-3" /> Statement
          </button>
        </div>
      </div>

      <ul className="space-y-2">
        {section.items.length === 0 && (
          <li className="text-xs text-gray-400 italic">No items — use the buttons above to add one.</li>
        )}
        {section.items.map((item, idx) => (
          <ItemRow
            key={item.id}
            item={item}
            index={idx}
            total={section.items.length}
            ticked={!!savedTick[item.id]}
            onToggleTick={() => onToggleTick(item.id)}
            onCommitText={(text) => onCommitItemText(section.id, item.id, text)}
            onReorder={(newIdx) => onReorderItem(section.id, item.id, newIdx)}
            onDelete={() => onDeleteItem(section.id, item.id)}
          />
        ))}
      </ul>
    </div>
  );
});

/* --------------------------------------------------------------------------
 * Main viewer
 * ------------------------------------------------------------------------ */
const DisciplinaryViewerModal = ({ isOpen, initialData, onClose, onSaveRecent, onDataChange }) => {
  const [data, setData] = useState(initialData);
  const [savedTick, setSavedTick] = useState({});
  const [saving, setSaving] = useState(false);

  // Body scroll lock
  useEffect(() => {
    if (!isOpen) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = original; };
  }, [isOpen]);

  // Sync from incoming prop (new generation, or recents reopen)
  useEffect(() => {
    setData(initialData);
    if (initialData) setSavedTick({});
  }, [initialData]);

  // Debounced autosave (500ms idle) — avoids JSON.stringify on every keystroke
  useEffect(() => {
    if (!data) return;
    const t = setTimeout(() => {
      try {
        sessionStorage.setItem(VIEWER_DRAFT_KEY, JSON.stringify(data));
      } catch (_) { /* noop */ }
      if (onDataChange) onDataChange(data);
    }, 500);
    return () => clearTimeout(t);
  }, [data, onDataChange]);

  // ---- Stable handlers (must be declared BEFORE any early return so the
  //      hooks order is identical across renders). ----
  const commitItemText = useCallback((sectionId, itemId, newText) => {
    setData(prev => ({
      ...prev,
      sections: prev.sections.map(s =>
        s.id !== sectionId ? s : { ...s, items: s.items.map(it => it.id !== itemId ? it : { ...it, text: newText }) }
      ),
    }));
  }, []);
  const deleteItem = useCallback((sectionId, itemId) => {
    setData(prev => ({
      ...prev,
      sections: prev.sections.map(s =>
        s.id !== sectionId ? s : { ...s, items: s.items.filter(it => it.id !== itemId) }
      ),
    }));
  }, []);
  const addItem = useCallback((sectionId, kind) => {
    const newItem = { id: `new_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, type: kind, text: '' };
    setData(prev => ({
      ...prev,
      sections: prev.sections.map(s =>
        s.id !== sectionId ? s : { ...s, items: [...s.items, newItem] }
      ),
    }));
  }, []);
  const reorderItem = useCallback((sectionId, itemId, newIndex) => {
    setData(prev => ({
      ...prev,
      sections: prev.sections.map(s => {
        if (s.id !== sectionId) return s;
        const currentIdx = s.items.findIndex(it => it.id === itemId);
        if (currentIdx < 0) return s;
        const target = Math.max(0, Math.min(s.items.length - 1, newIndex));
        if (target === currentIdx) return s;
        const next = s.items.slice();
        const [moved] = next.splice(currentIdx, 1);
        next.splice(target, 0, moved);
        return { ...s, items: next };
      }),
    }));
  }, []);
  const toggleTick = useCallback((itemId) => setSavedTick(prev => ({ ...prev, [itemId]: !prev[itemId] })), []);
  const commitSummary = useCallback((newText) => {
    setData(prev => ({ ...prev, prior_summary: newText }));
  }, []);

  if (!isOpen || !data) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      const ok = await onSaveRecent(data);
      if (ok) toast.success('Saved to recents (this session only)');
    } finally {
      setSaving(false);
    }
  };

  const handleExportPdf = () => {
    try {
      const doc = new jsPDF({ unit: 'mm', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const marginX = 18;
      const contentW = pageW - marginX * 2;
      // Use Times for a more formal, professional look — common in HR / legal
      // documents. Falls back to the built-in Times Roman bundled with jsPDF.
      const FONT = 'times';
      let y = 20;

      const ensureSpace = (h) => {
        if (y + h > pageH - 22) { doc.addPage(); y = 20; }
      };

      // ---------- Title ----------
      doc.setFont(FONT, 'bold');
      doc.setFontSize(18);
      doc.setTextColor(35, 35, 35);
      doc.text('Disciplinary Hearing — Chair Script', marginX, y);
      y += 9;

      // ---------- Grey header box with all metadata ----------
      const k = data.key_info || {};
      const headerRows = [
        ['Employee',  k.employee_name || '—',  'Date',     k.date || '—'],
        ['Manager',   k.manager_name  || '—',  'Start',    k.start_time || '—'],
        ['Notetaker', k.notetaker     || '—',  'Location', k.location || '—'],
      ];
      const rowH = 6.5;
      const padX = 4;
      const padY = 4;
      const labelW = 22;       // width of each label column
      const colGap = 6;        // gap between the two label-value pairs
      const halfW = (contentW - padX * 2 - colGap) / 2;
      const valueW = halfW - labelW;
      const boxH = padY * 2 + rowH * headerRows.length;

      ensureSpace(boxH + 6);
      doc.setDrawColor(190); doc.setFillColor(238, 238, 238);
      doc.roundedRect(marginX, y, contentW, boxH, 2, 2, 'FD');

      headerRows.forEach((row, i) => {
        const ry = y + padY + rowH * i + rowH * 0.7;
        // Left pair
        doc.setFont(FONT, 'bold'); doc.setFontSize(10); doc.setTextColor(90);
        doc.text(`${row[0]}`, marginX + padX, ry);
        doc.setFont(FONT, 'normal'); doc.setTextColor(20);
        const v1 = doc.splitTextToSize(String(row[1]), valueW)[0] || '';
        doc.text(v1, marginX + padX + labelW, ry);
        // Right pair
        const rightX = marginX + padX + halfW + colGap;
        doc.setFont(FONT, 'bold'); doc.setTextColor(90);
        doc.text(`${row[2]}`, rightX, ry);
        doc.setFont(FONT, 'normal'); doc.setTextColor(20);
        const v2 = doc.splitTextToSize(String(row[3]), valueW)[0] || '';
        doc.text(v2, rightX + labelW, ry);
      });
      y += boxH + 8;

      // ---------- Optional summary block ----------
      const summary = (data.prior_summary || '').trim();
      if (summary) {
        doc.setFont(FONT, 'bold'); doc.setFontSize(11); doc.setTextColor(40, 80, 140);
        ensureSpace(7);
        doc.text('Prior Meeting Summary', marginX, y);
        y += 5;
        doc.setFont(FONT, 'italic'); doc.setFontSize(10.5); doc.setTextColor(50);
        const sLines = doc.splitTextToSize(summary, contentW);
        sLines.forEach(line => {
          ensureSpace(5.5);
          doc.text(line, marginX, y);
          y += 5.2;
        });
        y += 5;
      }

      // ---------- Sections ----------
      const lineGap = 5.4;       // spacing between wrapped lines within an item
      const itemGap = 4.2;       // extra spacing between items
      const sectionGap = 6;      // extra spacing after a section

      data.sections.forEach((section, sIdx) => {
        // Start FACT-CONFIRMATION QUESTIONS on a new page so the script
        // breaks naturally between the procedural opening and the questions.
        const isFactSection = section.title && section.title.toLowerCase().includes('fact-confirmation');
        if (isFactSection && sIdx > 0) {
          doc.addPage();
          y = 20;
        }

        ensureSpace(14);
        doc.setFont(FONT, 'bold'); doc.setFontSize(12.5);
        doc.setTextColor(238, 114, 3);
        doc.text(section.title.toUpperCase(), marginX, y);
        y += 4.5;
        doc.setDrawColor(238, 114, 3); doc.setLineWidth(0.5);
        doc.line(marginX, y, marginX + contentW, y);
        y += 5.5;

        section.items.forEach((it, idx) => {
          const text = (it.text || '').trim();
          if (!text) return;
          // Leave 14mm on the left for number + checkbox
          const wrapped = doc.splitTextToSize(text, contentW - 16);
          const itemH = wrapped.length * lineGap + itemGap;
          ensureSpace(itemH);
          // Number (left, dark grey, bold)
          doc.setFont(FONT, 'bold'); doc.setFontSize(10.5); doc.setTextColor(70);
          doc.text(`${idx + 1}.`, marginX, y);
          // Checkbox
          doc.setDrawColor(140); doc.setLineWidth(0.35);
          doc.rect(marginX + 7, y - 3.2, 3.8, 3.8);
          // Text
          doc.setFont(FONT, 'normal'); doc.setFontSize(11); doc.setTextColor(30);
          wrapped.forEach((line, li) => {
            doc.text(line, marginX + 14, y + li * lineGap);
          });
          y += itemH;
        });
        y += sectionGap;
      });

      // ---------- Footer ----------
      ensureSpace(12);
      doc.setFont(FONT, 'italic'); doc.setFontSize(8.5); doc.setTextColor(140);
      doc.text(`Generated ${new Date().toLocaleString()}`, marginX, pageH - 12);

      // ---------- Embed full JSON in PDF metadata ----------
      // The "Upload from saved PDF" flow on the toolbar reads this back so
      // a manager can re-open the exact same script (with all edits) by
      // dropping the saved PDF + entering their login password. Mirrors the
      // pattern used by Quotes / Interview Notes (`<KEY>:<base64-json>`
      // stored in the PDF subject field).
      try {
        const json = JSON.stringify(data);
        const b64 = (typeof window !== 'undefined' && window.btoa)
          ? window.btoa(unescape(encodeURIComponent(json)))
          : '';
        doc.setProperties({
          title: `Disciplinary Hearing - ${k.employee_name || 'employee'}`,
          subject: `DISC_DATA_V1:${b64}`,
          keywords: 'encon disciplinary hearing chair script',
        });
      } catch (_) { /* metadata is best-effort, never block the download */ }

      const fname = `Disciplinary_${(k.employee_name || 'employee').replace(/[^a-z0-9]/gi, '_')}_${(k.date || '').replace(/-/g, '')}.pdf`;
      doc.save(fname);
      toast.success('PDF downloaded');
    } catch (e) {
      console.error('PDF export failed', e);
      toast.error('Failed to export PDF');
    }
  };

  // ---------- Word (.docx) export ----------
  // Same content as the PDF, but with blank space under each item so the
  // notetaker can type their notes inline next to / underneath each
  // question. Plain layout — no fancy borders/colours so the document is
  // easy to edit and re-print after the meeting.
  const handleExportWord = async () => {
    try {
      const docx = await import('docx');
      const {
        Document, Packer, Paragraph, TextRun, AlignmentType,
        HeadingLevel, Table, TableRow, TableCell, BorderStyle, WidthType,
      } = docx;

      const orange = 'EE7203';
      const grey = '4B4B4B';
      const noBorder = { style: BorderStyle.NONE, size: 0, color: 'FFFFFF' };
      const allBordersNone = { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder };

      const children = [];

      // Title
      children.push(new Paragraph({
        children: [new TextRun({
          text: 'Disciplinary Hearing — Chair Script',
          bold: true, size: 36, color: '232323', font: 'Calibri',
        })],
        spacing: { after: 200 },
      }));

      // Meta header table (2 cols × 3 rows like the PDF)
      const metaRow = (l1, v1, l2, v2) => new TableRow({
        children: [
          new TableCell({ width: { size: 18, type: WidthType.PERCENTAGE }, borders: allBordersNone,
            children: [new Paragraph({ children: [new TextRun({ text: l1, bold: true, color: grey, size: 20, font: 'Calibri' })] })] }),
          new TableCell({ width: { size: 32, type: WidthType.PERCENTAGE }, borders: allBordersNone,
            children: [new Paragraph({ children: [new TextRun({ text: v1 || '—', size: 20, font: 'Calibri' })] })] }),
          new TableCell({ width: { size: 18, type: WidthType.PERCENTAGE }, borders: allBordersNone,
            children: [new Paragraph({ children: [new TextRun({ text: l2, bold: true, color: grey, size: 20, font: 'Calibri' })] })] }),
          new TableCell({ width: { size: 32, type: WidthType.PERCENTAGE }, borders: allBordersNone,
            children: [new Paragraph({ children: [new TextRun({ text: v2 || '—', size: 20, font: 'Calibri' })] })] }),
        ],
      });

      children.push(new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: [
          metaRow('Employee', k.employee_name, 'Date', k.date),
          metaRow('Manager', k.manager_name, 'Start', k.start_time),
          metaRow('Notetaker', k.notetaker, 'Location', k.location),
        ],
      }));
      children.push(new Paragraph({ text: '', spacing: { after: 120 } }));

      // Optional summary
      const summary = (data.prior_summary || '').trim();
      if (summary) {
        children.push(new Paragraph({
          children: [new TextRun({ text: 'Prior Meeting Summary', bold: true, color: '28508C', size: 24, font: 'Calibri' })],
          spacing: { after: 80 },
        }));
        children.push(new Paragraph({
          children: [new TextRun({ text: summary, italics: true, size: 22, font: 'Calibri', color: '323232' })],
          spacing: { after: 200 },
        }));
      }

      // Sections
      data.sections.forEach((section) => {
        children.push(new Paragraph({
          children: [new TextRun({
            text: (section.title || '').toUpperCase(),
            bold: true, size: 26, color: orange, font: 'Calibri',
          })],
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 240, after: 120 },
        }));

        section.items.forEach((it, idx) => {
          const text = (it.text || '').trim();
          if (!text) return;

          // Question / statement line
          children.push(new Paragraph({
            children: [
              new TextRun({ text: `${idx + 1}. `, bold: true, size: 22, color: grey, font: 'Calibri' }),
              new TextRun({ text, size: 22, font: 'Calibri', color: '1E1E1E' }),
            ],
            spacing: { after: 60 },
          }));

          // "Notes:" label + a blank paragraph for the notetaker to type
          // into. Keeping it as one editable paragraph (rather than fixed
          // empty lines) lets the doc grow naturally as they type while
          // still showing visible empty space straight off the bat.
          children.push(new Paragraph({
            children: [new TextRun({ text: 'Notes:', italics: true, size: 20, color: grey, font: 'Calibri' })],
            spacing: { after: 60 },
          }));
          children.push(new Paragraph({ text: '', spacing: { after: 60 } }));
          children.push(new Paragraph({ text: '', spacing: { after: 60 } }));
          children.push(new Paragraph({ text: '', spacing: { after: 200 } }));
        });
      });

      // Footer
      children.push(new Paragraph({
        children: [new TextRun({
          text: `Generated ${new Date().toLocaleString()}`,
          italics: true, size: 16, color: '8C8C8C', font: 'Calibri',
        })],
        alignment: AlignmentType.LEFT,
        spacing: { before: 240 },
      }));

      const docx_doc = new Document({ sections: [{ properties: {}, children }] });
      const blob = await Packer.toBlob(docx_doc);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const fname = `Disciplinary_${(k.employee_name || 'employee').replace(/[^a-z0-9]/gi, '_')}_${(k.date || '').replace(/-/g, '')}.docx`;
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Word document downloaded');
    } catch (e) {
      console.error('Word export failed', e);
      toast.error('Failed to export Word document');
    }
  };

  const k = data.key_info || {};

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose} data-testid="disc-viewer-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden" style={{ border: `3px solid ${enconOrange}` }}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3 shrink-0">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-extrabold text-gray-900 truncate">
              Disciplinary Hearing — {k.employee_name || '(employee)'}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5 truncate">
              {k.date} · {k.start_time} · {k.location || 'no location set'}
              <span className="ml-2 text-[10px] text-gray-400">· auto-saved</span>
            </p>
          </div>
          <button onClick={handleExportPdf} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-white text-sm font-semibold shadow whitespace-nowrap" style={{ backgroundColor: enconOrange }} data-testid="disc-export-pdf">
            <Download className="w-4 h-4" /> Download PDF
          </button>
          <button onClick={handleExportWord} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-white text-sm font-semibold shadow whitespace-nowrap" style={{ backgroundColor: '#2A579A' }} data-testid="disc-export-word" title="Download a Word version with blank space under each question for notes">
            <Download className="w-4 h-4" /> Download Word
          </button>
          <button onClick={handleSave} disabled={saving} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border-2 text-sm font-semibold disabled:opacity-50 whitespace-nowrap" style={{ borderColor: enconOrange, color: enconOrange }} data-testid="disc-save-recent">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </button>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 ml-1" data-testid="disc-viewer-close" title="Close (your work is auto-saved)">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          <div className="rounded-xl p-3 bg-blue-50 border border-blue-200">
            <div className="text-[11px] font-bold uppercase tracking-wide mb-1 text-blue-900">
              Prior meeting summary
            </div>
            <AutoGrowTextarea
              initialValue={data.prior_summary || ''}
              onCommit={commitSummary}
              placeholder="Add or edit a summary of the prior investigation/meeting…"
              className="w-full bg-transparent border-none outline-none text-xs text-blue-900"
              testid="disc-summary-edit"
            />
          </div>

          {data.sections.map((section) => (
            <Section
              key={section.id}
              section={section}
              savedTick={savedTick}
              onAddItem={addItem}
              onDeleteItem={deleteItem}
              onCommitItemText={commitItemText}
              onToggleTick={toggleTick}
              onReorderItem={reorderItem}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default DisciplinaryViewerModal;
export { VIEWER_DRAFT_KEY };
